import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useGoogleLogin } from '@react-oauth/google';
import { userAuthService } from '../services/userAuthService';
import { motion, AnimatePresence } from 'framer-motion';
import { XMarkIcon, ExclamationTriangleIcon, CheckCircleIcon, ArrowPathIcon, SparklesIcon } from '@heroicons/react/24/outline';
import logoImage from '../assets/logo.png';

const ClientLogin: React.FC = () => {
    const { actualTheme } = useTheme();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const isDark = actualTheme === 'dark';

    // Google Login Handler
    const googleLoginHandler = useGoogleLogin({
        onSuccess: async (tokenResponse) => {
            try {
                setLoading(true);
                setError(null);
                const response = await userAuthService.googleLogin(tokenResponse.access_token, 'accessToken');
                if (response.success && response.user) {
                    setSuccess('Welcome back to your adventure!');
                    setTimeout(() => {
                        navigate('/');
                    }, 1500);
                } else {
                    setError(response.message || 'Authentication unsuccessful. Please try again.');
                }
            } catch (err: any) {
                setError(err.message || 'Authentication encountered an error.');
            } finally {
                setLoading(false);
            }
        },
        onError: () => setError('Google Sign-In was cancelled or failed.')
    });

    const handleGoogleSignIn = () => {
        setError(null);
        googleLoginHandler();
    };

    return (
        <div className="fixed inset-0 z-[11000] flex items-center justify-center p-4 sm:p-6 bg-slate-50 overflow-hidden">
            {/* Subtle decorative orbs for the liquid glass to reflect */}
            <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-100/50 rounded-full blur-[100px] pointer-events-none" />
            <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-blue-50/50 rounded-full blur-[100px] pointer-events-none" />

            {/* Liquid Glass Modal */}
            <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 10 }}
                className="relative w-full max-w-sm flex flex-col rounded-[2.5rem] shadow-[0_20px_60px_-15px_rgba(0,0,0,0.1)] overflow-hidden bg-white/60 backdrop-blur-2xl border border-white/80"
            >
                {/* Top Accent Bar */}
                <div className="absolute top-0 left-0 w-full h-1.5 bg-gradient-to-r from-blue-400 via-blue-600 to-blue-400" />
                
                {/* Close Button */}
                <button
                    onClick={() => navigate('/')}
                    className="absolute top-5 right-5 z-50 p-2 rounded-full bg-white/50 hover:bg-white text-slate-500 hover:text-slate-700 backdrop-blur-md transition-all active:scale-90 border border-white/50"
                >
                    <XMarkIcon className="w-5 h-5" />
                </button>

                <div className="px-8 py-12 flex flex-col items-center">
                    
                    {/* Header Item */}
                    <div className="flex flex-col items-center mb-10">
                        <div className="w-32 h-32 sm:w-36 sm:h-36 rounded-full flex items-center justify-center mb-7 shadow-xl p-5 bg-white/80 backdrop-blur-sm border border-white">
                            <img
                                src={logoImage}
                                alt="SightTrails"
                                className="w-full h-full object-contain"
                            />
                        </div>
                        
                        <div className="text-center">
                            <h2 className="text-2xl font-bold tracking-tight mb-2 text-slate-900">
                                Welcome Back
                            </h2>
                            <p className="text-sm font-medium text-slate-500">
                                Sign in to access your itinerary
                            </p>
                        </div>
                    </div>

                    {/* Status area */}
                    <AnimatePresence mode="wait">
                        {error && (
                            <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.9 }} className="w-full p-4 rounded-2xl mb-8 flex items-center space-x-3 bg-red-50 text-red-600 border border-red-100">
                                <ExclamationTriangleIcon className="w-5 h-5 shrink-0" />
                                <span className="text-xs font-bold leading-tight">{error}</span>
                            </motion.div>
                        )}
                        {success && (
                            <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.9 }} className="w-full p-4 rounded-2xl mb-8 flex items-center space-x-3 bg-emerald-50 text-emerald-600 border border-emerald-100">
                                <CheckCircleIcon className="w-5 h-5 shrink-0" />
                                <span className="text-xs font-bold leading-tight">{success}</span>
                            </motion.div>
                        )}
                    </AnimatePresence>

                    {/* Action Block */}
                    <div className="w-full flex justify-center mt-4">
                        <button
                            onClick={handleGoogleSignIn}
                            disabled={loading || !!success}
                            className={`w-full max-w-[280px] relative py-3.5 px-6 rounded-full flex items-center justify-center transition-all duration-300 font-bold border ${loading || !!success ? 'opacity-50 cursor-not-allowed bg-gray-50 border-gray-200' : 'hover:scale-[1.02] active:scale-95 bg-white border-slate-200 shadow-sm text-slate-800 hover:border-blue-200 hover:shadow-md'}`}
                        >
                            {loading ? (
                                <ArrowPathIcon className="w-6 h-6 animate-spin text-blue-600" />
                            ) : (
                                <div className="flex items-center gap-3">
                                    <svg className="w-5 h-5" viewBox="0 0 24 24">
                                        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                                        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                                        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                                        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                                    </svg>
                                    <span>Continue with Google</span>
                                </div>
                            )}
                        </button>
                    </div>

                    <p className="mt-8 text-[10px] font-bold uppercase tracking-[0.2em] text-center text-slate-400">
                        SightTrails Safaris
                    </p>
                </div>
            </motion.div>
        </div>
    );
};

export default ClientLogin;
